import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">请检查链接是否正确</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{category.name} - po文学创作指南 - po文学指南</title>
        <meta name="description" content={`po文学${category.name}专题，${category.description}。汇集优质po文创作教程与深度分析，涵盖入门到进阶全阶段内容。`} />
        <meta name="keywords" content={`po文学,${category.name},po文创作,网络文学,写作教程`} />
        <link rel="canonical" href={`https://powenxue.com/category/${slug}`} />
        <meta property="og:title" content={`${category.name} - po文学创作指南`} />
        <meta property="og:description" content={category.description} />
        <meta property="og:url" content={`https://powenxue.com/category/${slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${category.name} - po文学创作指南`} />
        <meta name="twitter:description" content={category.description} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <div className="mt-6 mb-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
              <i className={`${category.icon} text-accent-600 text-xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {category.name}
              </h1>
              <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {categoryArticles.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
              <i className="ri-file-list-3-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="font-heading text-lg font-semibold text-foreground-700 mb-2">暂无内容</h2>
            <p className="text-foreground-500 text-sm">该分类下还没有文章，敬请期待</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-md bg-background-50/90 backdrop-blur-sm text-foreground-700 text-xs font-medium">
                      {article.readTime}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h2 className="font-heading font-semibold text-foreground-950 text-base mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-background-200/60">
                    <span className="text-xs text-foreground-400">{article.author}</span>
                    <span className="text-xs text-foreground-300">{article.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}